<template>
  <div class="summary">
    <currency-select v-if="!isAuth" data-attr="cs-currency-select" index="cs"/>

    <h2>{{ $t('cartSummary.totalDue')}}:</h2>

    <ul class="summary-list">
      <li
          v-for="(item, key) in summaryItems"
          :key="key"
          :data-attr="`cs-${item.key}`"
          v-show="item.count > 0"
      >
        <div>
          <h3 class="title">{{item.title}} : <b>{{item.count}}</b></h3>
          <div class="price">
            <span v-if="item.id === 'webHosting' && freeProduct" class="old-price">
              {{ numberTransform(item.price) }}
            </span>

            <span
              v-html="priceTransform(item.id === 'webHosting' && freeProduct ? 0 : numberTransform(item.price), currencyInfo.suffix, '', currencyInfo.prefix)"
              :data-attr="`price-${item.key}`"
              :data-attr-price="`${currencyInfo.prefix || ''}${item.price} ${currencyInfo.suffix || ''}`"
            ></span>
          </div>
          <div
            v-if="Number(item.renewal) > 0"
            class="renewal"
            :data-attr-price="`${currencyInfo.prefix || ''}${item.renewal} ${currencyInfo.suffix || ''}`"
            >
            {{$t('renewalPrice')}} {{currencyInfo.prefix}}{{numberTransform(item.renewal)}} {{currencyInfo.suffix}}
          </div>
        </div>
      </li>
    </ul>

    <v-expansion-panels class="promo-panel" v-model="panel" multiple>
      <v-expansion-panel>
        <v-expansion-panel-header v-slot="{ open }" data-attr="promo-activator">
          <div class="trigger">
            <div>{{ $t('cartSummary.addPromo') }}<br></div>
            <div
                v-if="Number(discount) > 0 && !open"
                class="discount"
                data-attr="discount-added"
                :data-attr-price="`${currencyInfo.prefix || ''}${discount} ${currencyInfo.suffix || ''}`"
            >{{ $t('cartSummary.discount') }}:&nbsp;{{currencyInfo.prefix}}<b>{{discount}}</b> {{currencyInfo.suffix}}</div>
          </div>
        </v-expansion-panel-header>
        <v-expansion-panel-content>
          <v-form ref="form" class="promo-holder" @submit.prevent="checkPromo">
            <v-text-field
                v-model="promo"
                :loading="loading"
                outlined
                clearable
                data-attr="promo"
                autofocus
            >
              <template v-slot:message="{ message }">
                <span v-html="message"></span>
              </template>
            </v-text-field>
            <v-btn
                :ripple="false"
                @click="checkPromo"
                :loading="loading"
                color="primary"
                data-attr="promo-btn"
            >
              <i class="sr-icon-plus"></i>
            </v-btn>
          </v-form>

          <v-expand-transition>
            <template v-if="promo && isAvailablePromo !== undefined">
              <div v-if="isAvailablePromo" class="info-message valid" data-attr="promo-valid">
                <v-icon class="sr-icon-check"/>
                <span
                    v-html="$t('cartSummary.promoSuccess', {promo: promo, discount: discount, currencySuffix: currencyInfo.suffix, currencyPrefix: currencyInfo.prefix})"
                    data-attr="discount"
                    :data-attr-price="`${currencyInfo.prefix || ''}${discount} ${currencyInfo.suffix || ''}`"
                ></span>
              </div>
              <div v-else class="info-message invalid" data-attr="promo-invalid">
                <v-icon class="sr-icon-close"/>
                <span v-html="$t('validation.promo.'+promoError, {promo: promo, periods: variablesList(promoCycles)})"></span>
              </div>
            </template>
          </v-expand-transition>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>

    <div class="total">
      <div class="head">{{ $t('cartSummary.total') }}</div>
      <div class="price-row">
        <v-tooltip left max-width="330">
          <template v-slot:activator="{ on }">
            <i class="sr-icon-lock" v-on="on"></i>
          </template>
          <span>{{ $t('cartSummary.infoProtection') }}</span>
        </v-tooltip>
        <span
            v-html="priceTransform(totalPrice,  currencyInfo.suffix, '', currencyInfo.prefix)"
            class="price"
            :data-attr-price="`${currencyInfo.prefix || ''}${totalPrice} ${currencyInfo.suffix || ''}`"
        ></span>
      </div>

    </div>

    <v-expand-transition>
      <div class="continue" v-show="showContinueBtn">
        <v-btn
            @click="$emit('continue-click')"
            :ripple="false"
            color="primary"
        >{{ $t('actions.continue') }}</v-btn>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
  import { mapGetters, mapActions } from "vuex";
  import { checkPromoCode } from "@/store/api/settings";
  import { DOMAIN_TYPE, PROMO, PROMO_TYPE } from '@/constants';
  import { numberTransform } from '@/modules/numberTransform';
  import { priceTransform } from '@/modules/priceTransform';
  import CurrencySelect from '@/shared/components/CurrencySelect/CurrencySelect.vue';
  import { CART_GETTERS, CART_ACTIONS, CART_MUTATIONS } from "@/store/modules/cart-context";
  import { SETTINGS_GETTERS, SETTING_ACTIONS, SETTINGS_MUTATIONS } from "@/store/modules/settings";
  import * as Sentry from "@sentry/vue";

  export default {
    name: "MultiDomainCartSummary",
    model: {
      prop: 'items'
    },
    DOMAIN_TYPE,
    components: { CurrencySelect },
    props: {
      summaryList: { type: Array, default: () => ([]) },
      currencyInfo: { type: [Object, Array], default: () => ({}) },
      routeQuery: { type: Object, default: () => ({promo: ''}) },
      paymentPeriods: { type: Array, default: () => ([]) },
      payType: { type: String },
      domainType: { type: String },
      monthlyDiscount40: { type: Boolean, default: false },
      showContinueBtn: { type: Boolean, default: true }
    },

    data() {
      return {
        loading: false,
        promo: '',
        panel: undefined,
        discount: 0,
        discountRenewal: 0,
        isAvailablePromo: undefined,
        promoError: undefined,
        promoResponse: undefined,
        freeProduct: false,
        promoCycles: [],
        totalPrice: 0,
        summaryItems: [
          { id: 'domainRegistration', title: this.$t('bulk.domainRegistration'), count: 0, price: 0, renewal: 0 },
          { id: 'whoisProtection', title: this.$t('bulk.whoisProtection'), count: 0, price: 0, renewal: 0 },
          { id: 'webHosting', title: this.$t('bulk.webHosting'), count: 0, price: 0, renewal: 0 },
          { id: 'sslCertificate', title: this.$t('bulk.sslCertificate'), count: 0, price: 0, renewal: 0 },
        ],
        previousWebHostingCount: 0
      }
    },

    mounted() {
      if (!this.cartContext) { this.cartContext = this.setCartContext(); }
      if (!this.cartExtra) { this.cartExtra = this.setCartExtra(); }
      if (!this.authorization) { this.authorization = this.setAuth(); }
    },

    computed: {
      ...mapGetters('cartContext', {
        cartContext: CART_GETTERS.CART_CONTEXT,
        cartExtra: CART_GETTERS.CART_EXTRA,
      }),
      ...mapGetters('settings', {
        authorization: SETTINGS_GETTERS.AUTH,
      }),
      isAuth() {
        return this.authorization && this.authorization.auth && !!Object.keys(this.authorization.auth).length || false;
      },
      freeTrial() {
        return this.isAvailablePromo && this.promo && this.promo.toLowerCase().includes(PROMO.FREE_TRIAL) || false;
      },
    },
    methods: {
      numberTransform,
      priceTransform,
      ...mapActions('cartContext', {
        setCartContext: CART_ACTIONS.SET_CART_CONTEXT,
        setCartExtra: CART_ACTIONS.SET_CART_EXTRA,
      }),
      variablesList(list){
        const boldList = list.map( period => `<b>${period}</b>` );
        return boldList.slice(0, -1).join(', ').concat(boldList.length > 1 ? ` ${this.$t('or')} ` : '', boldList.slice(-1));
      },
      prefilledPromo() {
        this.promo = this.routeQuery.promo;
        this.checkPromo();
        if (!this.promo) { this.panel = [1] };
      },
      async checkPromo() {
        this.promo = this.promo ? this.promo.trim() : '';
        if (!this.promo) return
        try {
          this.loading = true;
          this.promoResponse = await checkPromoCode(encodeURI(this.promo));
          this.isAvailablePromo = true;

          this.promoCycles = [];

          if (this.promoResponse?.cycles?.products?.length) {
            this.promoCycles.push(...this.promoResponse.cycles.products.map(el => this.$t(`paymentPeriods.${el}`)));
          }
          this.getPromo(this.promoResponse);
        } catch (error) {
          Sentry.captureException(error);
          this.isAvailablePromo = false;

          if(error.status === 404) {
            this.promoError = 'notExist';
          } else if(error.status === 410) {
            this.promoError = 'expired';
          } else if (error.status === 400) {
            this.promoError = 'maxusesReached'
          } else if (error.status === 425) {
            this.promoError = 'tooEarly'
          } else {
            this.promoError = 'notExist';
          }
        } finally {
          this.$emit('currentPromo', this.promo);
          this.loading = false;
        }
      },
      getPromo(promo) {
        this.discount = 0;
        this.discountRenewal = 0;
        this.promoError = undefined;

        this.getPromoStatus(promo);
      },
      getPromoStatus(promo) {
        this.isAvailablePromo = true;
        const paymentPeriodsArr = [];

        Object.values(this.paymentPeriods).forEach(item => paymentPeriodsArr.push(item.period));
        const periodsHasCycles = promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && promo.cycles.products.some(product => paymentPeriodsArr?.includes(product));

        let hasRequiredCycle = false;
        hasRequiredCycle = (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) || hasRequiredCycle;

        this.cartContext.add_products.forEach(() => {
          hasRequiredCycle = (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) || hasRequiredCycle;
        });

        this.cartContext.domains.forEach((domain) => {
          const domainTld = domain.domain ? domain.domain.substr(domain.domain.indexOf('.') + 1) : '';
          hasRequiredCycle = (promo.appliesto.tld.includes(domainTld) && ((promo.cycles.products.length === 0 && promo.cycles.domains.length === 0) || promo.cycles.domains.includes(domain.billingcycle))) || hasRequiredCycle;
        });

        if ((!periodsHasCycles && !hasRequiredCycle) ||
          (this.isAuth && this.authorization.auth.active_orders > 0 && promo.newsignups) ||
          ((!this.isAuth || this.isAuth && this.authorization.auth.active_orders === 0) && promo.existingclient)
        ) {
          this.isAvailablePromo = false;
          this.promoError = 'cantUse';
        }

        else if (!hasRequiredCycle && periodsHasCycles) {
          this.isAvailablePromo = false;
          this.promoError = 'invalidPromo';
        }

        else if(!promo.cycles.products.includes('one time') && this.payType === DOMAIN_TYPE.ONETIME) {
          this.isAvailablePromo = false;
          this.promoError = 'invalidPeriod';
        }

        if (this.isAuth && promo.onceperclient && this.authorization.auth.used_promo.length > 0 && this.authorization.auth.used_promo.includes(promo.id)) {
          this.isAvailablePromo = false;
          this.promoError = 'oneTime';
        }

        if (this.isAvailablePromo) {
          this.getPromoValue(promo);
        }
      },
      getPromoValue(promo) {
        const domainObj = this.summaryItems.find((el) => el.id === 'domainRegistration');
        const whoisProtection = this.summaryItems.find((el) => el.id === 'whoisProtection');
        const domainPrice = (domainObj?.price || 0) + (whoisProtection?.price || 0);
        const domainPriceRenewal = (domainObj?.renewal || 0) + (whoisProtection.renewal || 0);

        const productPrice = this.summaryItems.find((el) => el.id === 'webHosting').price || 0;
        const productUpsellPrice = this.summaryItems?.find((el) => el.id === 'sslCertificate')?.price || 0;

        let discount = 0;
        let discountRenewal = 0;

        switch (promo?.type) {
          case PROMO_TYPE.PERCENTAGE:
            this.cartContext.products.forEach((product) => {
              if (promo.appliesto.pid.includes(product.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(product?.billingcycle))) {
                const productDiscount = (productPrice + productUpsellPrice) * promo.value / 100;
                discount += productDiscount;
              }
            });

            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) {
                discount += prod.price * promo.value / 100;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += domainPrice * promo.value / 100;
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += domainPriceRenewal * promo.value / 100;
                }
              }
            });
            break;
          case PROMO_TYPE.FIXED_AMOUNT:
            this.cartContext.products.forEach((product) => {
              if (promo.appliesto.pid.includes(product?.pid) && (promo.cycles.products.length === 0 ||
                promo.cycles.products.includes(product?.billingcycle))) {
                discount += this.convertCurrency(promo.value);
              }
            })

            this.cartContext.add_products.forEach(() => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) {
                discount += this.convertCurrency(promo.value);
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += this.convertCurrency(promo.value);
              }
            });
            break;
          case PROMO_TYPE.PRICE_OVERRIDE:
            this.cartContext.products.forEach((product) => {
              if (promo.appliesto.pid.includes(product?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(product?.billingcycle))) {
                const val = productPrice < this.convertCurrency(promo.value) ? productPrice : productPrice - this.convertCurrency(promo.value);
                discount += val;
              }
            });

            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) {
                const val = prod.price < this.convertCurrency(promo.value) ? prod.price : prod.price - this.convertCurrency(promo.value);
                discount += val;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                const calculateDiscount = (price) => price < this.convertCurrency(promo.value)
                  ? price
                  : price - this.convertCurrency(promo.value);
                discount += calculateDiscount(domainPrice);
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += calculateDiscount(domainPriceRenewal);
                }
              }
            });
            break;
          case PROMO_TYPE.FREE_SETUP:
            this.cartContext.products.forEach((product) => {
              if (promo.appliesto.pid.includes(product?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(product?.billingcycle))) {
                discount += productPrice;
              }
            });

            this.cartContext.add_products.forEach((prod) => {
              if (promo.appliesto.pid.includes(this.cartContext.products[0]?.pid) && (promo.cycles.products.length === 0 || promo.cycles.products.includes(this.cartContext.products[0]?.billingcycle))) {
                discount += prod.price;
              }
            });

            this.freeProduct = discount >= productPrice;

            this.cartContext.domains.forEach((domain) => {
              const domainTld = domain.domain ? domain.domain.substr( domain.domain.indexOf('.') + 1 ) : '';
              if (promo.appliesto.tld.includes(domainTld) && (promo.cycles.domains.length === 0 || promo.cycles.domains.includes(domain.billingcycle)) && !domainObj?.freeDomain) {
                discount += domainPrice;
                if (promo?.recurring && domainPrice !== domainPriceRenewal) {
                  discountRenewal += domainPriceRenewal;
                }
              }
            });
            break;
          default: break;
        }

        discount = discount ? (Math.round(discount * 100) / 100).toFixed(2) : '0.00';
        discountRenewal = discountRenewal ? (Math.round(discountRenewal * 100) / 100).toFixed(2) : discount;
        this.discount = discount;
        this.discountRenewal = discountRenewal;
        this.getTotalPrice();
      },
      convertCurrency(val) {
        const fixedValue = Object.values(this.promoResponse.fixed_values).find(item => item.currency === this.cartContext.currency);
        const value = Object.keys(this.currencyInfo).length ? val * this.currencyInfo.rate : fixedValue.price;
        return Number((Math.round(value * 100) / 100).toFixed(2));
      },
      getTotalPrice() {
        const domainRegistration = this.summaryItems?.find((el) => el.id === 'domainRegistration')?.price || 0;
        const whoisProtection = this.summaryItems?.find((el) => el.id === 'whoisProtection')?.price || 0;
        const webHosting = this.summaryItems?.find((el) => el.id === 'webHosting')?.price || 0;
        const productUpsellPrice = this.summaryItems?.find((el) => el.id === 'sslCertificate')?.price || 0;

        const sum = Number(domainRegistration + whoisProtection + webHosting + productUpsellPrice);
        this.discount = this.discount > sum ? sum : this.discount;
        const totalPrice = (Math.round((sum - this.discount) * 100) / 100).toFixed(2);
        this.$store.commit(`settings/${SETTINGS_MUTATIONS.SET_TOTAL_PRICE}`, totalPrice);
        this.totalPrice = totalPrice;

        const freeDomain = sum <= 0;
        this.$emit('freeDomain', freeDomain);
      },
      updateSummaryItems() {
        const items = {
          domainRegistration: { count: 0, price: 0, renewal: 0 },
          whoisProtection: { count: 0, price: 0, renewal: 0 },
          webHosting: { count: 0, price: 0, renewal: 0 },
          sslCertificate: { count: 0, price: 0, renewal: 0 },
        };

        this.summaryList?.forEach(entry => {
          const domain = entry.domain || {};
          const idprotection = entry.idprotection || { price: 0, renewal: 0 };
          const hostingGift = entry.hostingGift || { price: 0, renewal: 0 };
          const ssl = entry.ssl || { price: 0, renewal: 0 };

          if (domain.isValidDomain) {
            items.domainRegistration.count += 1;
            items.domainRegistration.price += Number(domain.price) || 0;
            items.domainRegistration.renewal += Number(domain.renewal) || Number(domain.price) || 0;
          }

          if (domain.isValidDomain && idprotection.value) {
            items.whoisProtection.count += 1;
            items.whoisProtection.price += idprotection.price || 0;
            items.whoisProtection.renewal += Number(idprotection.renewal) || Number(idprotection.price) || 0;
          }

          if (domain.isValidDomain && hostingGift.value) {
            items.webHosting.count += 1;
            items.webHosting.price += Number(hostingGift.price) || 0;
            items.webHosting.renewal += Number(hostingGift.renewal) || Number(hostingGift.price) || 0;
          }

          if (domain.isValidDomain && ssl.value) {
            items.sslCertificate.count += 1;
            items.sslCertificate.price += Number(ssl.price) || 0;
            items.sslCertificate.renewal += Number(ssl.renewal) || Number(ssl.price) || 0;
          }
        });

        this.summaryItems = [
          {
            id: 'domainRegistration',
            title: this.$t('bulk.domainRegistration'),
            ...items.domainRegistration,
          },
          {
            id: 'whoisProtection',
            title: this.$t('bulk.whoisProtection'),
            ...items.whoisProtection,
          },
          {
            id: 'webHosting',
            title: this.$t('bulk.webHosting'),
            ...items.webHosting,
          },
          {
            id: 'sslCertificate',
            title: this.$t('bulk.sslCertificate'),
            ...items.sslCertificate,
          },
        ];

        const domainsStore = this.summaryList
          .filter(entry => entry.domain?.isValidDomain === true)
          .map(entry => {

          const result = {
            domain: entry.domain?.name || '',
            billingcycle: entry.domain?.year,
            type: entry.type
          };

          if (entry.codeEpp) { result.codeEpp = entry.codeEpp; }
          result.idprotection = entry.idprotection?.value || false;
          result.customfields = entry?.customfields || {};

          return result;
        });

        this.cartContext.domains = domainsStore;

        const productsStore = this.summaryList.reduce((acc, entry) => {
          if (entry.hostingGift?.value === true) {
            acc.push({
              billingcycle: "monthly",
              config_opts: {},
              customfields: {},
              pid: 542,
              domain: entry.domain.name
            });
          }

          if (entry.ssl?.value === true) {
            acc.push({
              billingcycle: "annually",
              config_opts: {},
              customfields: {},
              pid: 217,
              domain: entry.domain.name
            });
          }

          return acc;
        }, []);

        this.cartContext.products = productsStore;

        this.getPromoValue(this.promo);
      }
    },
    watch : {
      promo() {
        this.promoResponse = undefined;
        this.isAvailablePromo = undefined;
        this.discount = 0;
        this.freeProduct = false;
        this.getTotalPrice();
        this.$emit('currentPromo', '');
      },
      isAvailablePromo(available) {
        this.$store.commit(`cartContext/${CART_MUTATIONS.SET_CART_CONTEXT}`, { promo: available === true ? this.promo : ''});
      },
      "authorization": {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
        },
        deep: true,
      },
      "cartContext": {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
        },
        deep: true,
      },
      "summaryList": {
        handler() {
          if(this.promoResponse) { this.getPromo(this.promoResponse) }
          this.updateSummaryItems();
          this.getTotalPrice();
        },
        deep: true,
      },
      freeTrial() {
        this.$emit('freeTrial', this.freeTrial);
      },
      promoError(val) {
        if(val && !(this.monthlyDiscount40 && this.cartContext.products[0]?.billingcycle !== 'monthly')) {
          this.panel = [0];
        }
      },
      'routeQuery.promo': {
        handler() {
          this.prefilledPromo();
        },
        deep: true,
        immediate: true,
      },
      "summaryItems": {
        deep: true,
        handler(newVal) {
          const webHostingItem = newVal.find(item => item.id === 'webHosting');
          if (!webHostingItem) return;

          const newCount = webHostingItem.count;
          if (newCount > this.previousWebHostingCount) {
            this.promo = PROMO.DOMAIN_GIFT;
            this.checkPromo();
          } else if (newCount < this.previousWebHostingCount && newCount === 0) {
            this.promo = '';
            this.panel = [1];
          }

          this.previousWebHostingCount = newCount;
        }
      }
    },
  }
</script>

<style lang="scss">
  .summary {
    .continue {
      text-align: center;
      padding-top: 40px;

      @media (max-width: $desktop) {
        display: none !important;
      }

      .v-btn {
        width: 100%;
      }
    }
  }
</style>
